import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SafeImage } from '@/components/ui/safe-image';
import { Camera, Package, Play, Video } from 'lucide-react';
import { ComplianceBadge } from '../ComplianceBadge';
import { MediaAvailabilityBadge } from '../MediaAvailabilityBadge';
import type { RetailerLocationContext } from '../../lib/retailerViewUtils';
import {
  campaignStatusLabel,
  isRetailerCampaign,
} from '../../lib/campaignBrowseUtils';

interface RetailerContextPanelProps {
  context: RetailerLocationContext | null;
  className?: string;
  onOpenWalkthrough?: (walkthroughId: string) => void;
}

export function RetailerContextPanel({
  context,
  className = '',
  onOpenWalkthrough,
}: RetailerContextPanelProps) {
  if (!context) {
    return (
      <aside className={`rounded-xl border bg-white p-4 text-sm text-gray-500 ${className}`}>
        Select a store area on the map to see campaigns and evidence for that location.
      </aside>
    );
  }

  const retailerCampaigns = context.campaigns.filter(isRetailerCampaign);

  return (
    <aside className={`rounded-xl border bg-white overflow-hidden flex flex-col max-h-[70vh] lg:max-h-none ${className}`}>
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="font-semibold text-gray-900">{context.locationName}</h2>
        <p className="text-xs text-gray-500 mt-0.5">Location details</p>
      </div>

      <div className="overflow-y-auto p-4 space-y-4">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Campaign history
          </h3>
          {retailerCampaigns.length === 0 ? (
            <p className="text-sm text-gray-500">No campaigns recorded at this location.</p>
          ) : (
            <ul className="space-y-2">
              {retailerCampaigns.map((campaign) => (
                <li key={campaign.id} className="rounded-lg border p-2.5 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{campaign.campaignName}</p>
                    {campaign.phase === 'current' ? (
                      <ComplianceBadge status={campaign.complianceStatus} />
                    ) : (
                      <Badge
                        variant="outline"
                        className={
                          campaign.phase === 'planned'
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-slate-50 text-slate-600'
                        }
                      >
                        {campaignStatusLabel(campaign)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {campaign.cycleCode}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {(campaign.mediaFormats?.length
                      ? campaign.mediaFormats
                      : [{ mediaFormatId: campaign.mediaFormatId ?? null, name: campaign.formatLabel, quantity: campaign.plannedQuantity }]
                    ).map((format) => (
                      <li
                        key={`${format.mediaFormatId ?? format.name}`}
                        className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600"
                      >
                        <span>{format.name}</span>
                        <span>×{format.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
            <Camera className="h-3.5 w-3.5" aria-hidden />
            Latest evidence
          </h3>
          {context.evidenceImages.length === 0 ? (
            <p className="text-sm text-gray-500">No photos for this location yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {context.evidenceImages.slice(0, 4).map((image) => (
                <div key={image.id} className="rounded-lg overflow-hidden border bg-gray-50">
                  <SafeImage
                    src={image.url}
                    alt={image.caption}
                    className="w-full aspect-square object-cover"
                    placeholderType="photo"
                    helperText=""
                  />
                  <div className="p-1.5">
                    <p className="text-[11px] text-gray-600 line-clamp-2">{image.caption}</p>
                    <p className="text-[10px] text-gray-400">{image.captureDate}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {context.mediaLocations.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
              <Package className="h-3.5 w-3.5" aria-hidden />
              Media locations
            </h3>
            <ul className="space-y-2">
              {context.mediaLocations.map((media) => (
                <li key={media.id} className="rounded-lg border p-2.5 text-sm flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{media.formatLabel}</p>
                    <p className="text-xs text-gray-500">Condition: {media.condition}</p>
                  </div>
                  <MediaAvailabilityBadge availability={media.availability} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {context.walkthroughs.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
              <Video className="h-3.5 w-3.5" aria-hidden />
              Walk-through videos
            </h3>
            <ul className="space-y-1.5">
              {context.walkthroughs.map((walk) => (
                <li key={walk.id}>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-auto justify-between gap-3 px-2.5 py-2 text-left"
                    onClick={() => onOpenWalkthrough?.(walk.id)}
                    disabled={!onOpenWalkthrough}
                    aria-label={`Play ${walk.title} walk-through`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{walk.title}</span>
                      <span className="block text-xs font-normal text-gray-500">{walk.captureDate}</span>
                    </span>
                    <Play className="h-4 w-4 shrink-0" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {context.previousEvidence.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Previous evidence</h3>
            <div className="flex gap-1.5 overflow-x-auto">
              {context.previousEvidence.slice(0, 6).map((image) => (
                <div key={image.id} className="shrink-0 w-16 h-16 rounded border overflow-hidden">
                  <SafeImage src={image.url} alt={image.caption} className="w-full h-full object-cover" placeholderType="photo" helperText="" />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </aside>
  );
}
